import { getWebPath } from '@doss/common-all'
import { MenuItem, Stack, Typography } from '@mui/material'
import { first } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { AiOutlineForm } from 'react-icons/ai'
import { RxChevronDown, RxLightningBolt, RxPlay } from 'react-icons/rx'
import { tss } from 'tss-react'
import {
  ActionStatus,
  BreadcrumbObjectType,
  WorkflowStatus,
  useExecuteActionManualTriggerMutation,
} from '~/common/generated/apollo-operations'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import Link from '~/components/shared/Link'
import { Button } from '~/components/ui/atoms/button/Button'
import { Menu } from '~/components/ui/atoms/menu/Menu'
import { useTypedTranslation } from '../../../common/hooks/useTypedTranslation'
import { DetailedWorkspaceActions } from './RenderWorkspace'

interface WorkspaceActionButtonsProps {
  actions: DetailedWorkspaceActions[]
}

/**
 * Renders a dropdown list of all the actions that can be taken for a workspace. If there is only
 * one action, it will render a single button instead.
 *
 * After clicking the button, either a form will open or an action will be triggered.
 */
export function WorkspaceActionButtons({ actions }: WorkspaceActionButtonsProps) {
  const snackbar = useDossSnackbar()
  const { t } = useTypedTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const isOpen = useMemo(() => Boolean(anchorEl), [anchorEl])
  const [executeTrigger] = useExecuteActionManualTriggerMutation()
  const visibleActions = actions.filter((actionButton) => {
    const action = actionButton.manualTrigger?.action
    if (!action) {
      return true
    }
    return action.status === ActionStatus.ACTIVE && action.workflow.status === WorkflowStatus.ACTIVE
  })

  const handleClickAction = async (action: DetailedWorkspaceActions) => {
    if (action.manualTrigger) {
      await executeTrigger({
        variables: { input: { actionManualTriggerId: action.manualTrigger.id } },
      }).catch(() =>
        snackbar.showError(t('actions.failed_to_execute', { actionName: action.name })),
      )
    }
    setAnchorEl(null)
  }

  const { classes } = useStyles()

  const firstAction = first(visibleActions)

  useEffect(() => {
    // Reset the anchor element so
    // the menu doesn't persist after
    // route changes from browser back button
    setAnchorEl(null)
  }, [actions])

  return (
    <>
      {visibleActions.length > 1 && (
        <>
          <Button
            className={classes.button}
            endIcon={<RxChevronDown />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            size="small"
            variant="primary"
          >
            {t('common.actions.actions')}
          </Button>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClose={() => setAnchorEl(null)}
            open={isOpen}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {visibleActions.map((action) => {
              // If routing to form, wrap the Menu Item in a Link component so user can right click
              // to open in a new tab
              const formId = action.formV2?.id ?? action.form?.id
              if (formId) {
                return (
                  <Link
                    key={action.id}
                    href={getWebPath({
                      objectType: BreadcrumbObjectType.FORM,
                      objectId: formId,
                    })}
                    style={{ textDecoration: 'none' }}
                  >
                    <MenuItem>
                      <Stack alignItems="center" direction="row" spacing={1}>
                        {action.manualTrigger ? <RxPlay /> : <AiOutlineForm />}
                        <Typography variant="caption">{action.name}</Typography>
                      </Stack>
                    </MenuItem>
                  </Link>
                )
              } else {
                return (
                  <MenuItem key={action.id} onClick={() => handleClickAction(action)}>
                    <Stack alignItems="center" direction="row" spacing={1}>
                      {action.manualTrigger ? <RxPlay /> : <AiOutlineForm />}
                      <Typography variant="caption">{action.name}</Typography>
                    </Stack>
                  </MenuItem>
                )
              }
            })}
          </Menu>
        </>
      )}
      {visibleActions.length === 1 &&
        firstAction &&
        (() => {
          const formId = firstAction.formV2?.id ?? firstAction.form?.id
          return (
            <Button
              component={formId ? Link : undefined}
              href={
                formId
                  ? getWebPath({ objectType: BreadcrumbObjectType.FORM, objectId: formId })
                  : undefined
              }
              onClick={formId ? undefined : () => handleClickAction(firstAction)}
              size="xsmall"
              startIcon={<RxLightningBolt />}
              variant="ghost"
            >
              {firstAction.name}
            </Button>
          )
        })()}
    </>
  )
}

const useStyles = tss.create({
  button: {
    '& .MuiButton-endIcon': {
      transition: 'transform 0.3s ease',
    },
    '&:hover .MuiButton-endIcon': {
      transform: 'translateY(1px)',
    },
  },
})
